import {
  GetCompanySearchResponse,
  PostApplicationRequestBody,
} from '@iwoca/lapi-client/edge';
import { currencyToNumber } from '@iwoca/orion';

import { TGetStartedDataDE } from './GetStartedDE/GetStartedDE';
import { TGetStartedDataUK } from './GetStartedUK/GetStartedUK';
import { getInitialStateBody } from './GetStartedUK/GetStartedUK.helpers';
import {
  fetchPutAndGetState,
  usePostApplicationMutation,
  usePostStateMutation,
  usePutApplicationMutation,
} from '../../api/lendingApi/lendingApiMutations';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../api/lendingApi/lendingApiQueries';
import { getUpdatedApplicationBody } from '../../helpers/getApplicationBody';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useLocale } from '../../hooks/useLocale';
import { useSignupParams } from '../../hooks/useSignupParams';
import { DATA_LAYER_EVENT, postToDataLayer } from '../../utils/googleDataLayer';
import {
  denormaliseGetStartedData,
  normaliseStepData,
} from '../helpers/stateNormalisation';
import { GET_STARTED_STEP_ID } from '../helpers/steps';

export const useSubmitGetStarted = () => {
  const { locale } = useLocale();
  const { state } = useGetStateQuery();
  const { application } = useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[GET_STARTED_STEP_ID],
  );

  const { affiliateTag, fromCampaign } = useSignupParams();
  const stateKeyFromContext = useContextStateKey();

  const { mutatePostState } = usePostStateMutation();
  const { mutatePostApplication } = usePostApplicationMutation();
  const { mutatePutApplication } = usePutApplicationMutation();

  const submitGetStarted = async ({
    applicationBody,
    hadCompleteApplication,
    values,
  }: {
    applicationBody: PostApplicationRequestBody;
    hadCompleteApplication: boolean;
    values:
      | TGetStartedDataDE
      | (TGetStartedDataUK & {
          prefilledCompany?: GetCompanySearchResponse['data'][number] | null;
        });
  }) => {
    let stateKeyForApplication: string | null | undefined = stateKeyFromContext;

    const stateBody = getInitialStateBody(affiliateTag, fromCampaign, {
      ...values,
    });

    // Submit new application and state
    if (!application && !normalisedState) {
      stateKeyForApplication = await mutatePostState({
        body: stateBody,
      });

      if (stateKeyForApplication) {
        await mutatePostApplication({
          stateKey: stateKeyForApplication,
          body: applicationBody,
        });
      }
    }
    // Submit new application, update state if state exists without email
    else if (!application && !normalisedState.email && stateKeyFromContext) {
      const statePutBody = denormaliseGetStartedData(state, values);
      await fetchPutAndGetState({
        body: statePutBody,
        stateKey: stateKeyFromContext,
      });

      await mutatePostApplication({
        body: applicationBody,
        stateKey: stateKeyFromContext,
      });
    }
    // Submit new application if state exists with email
    else if (!application && Boolean(normalisedState) && stateKeyFromContext) {
      await mutatePostApplication({
        stateKey: stateKeyFromContext,
        body: applicationBody,
      });
    }
    // Update state and/or application if they exists but email or amount was missing
    else if (
      Boolean(application) &&
      (!normalisedState.email || !hadCompleteApplication) &&
      stateKeyFromContext
    ) {
      if (!normalisedState.email) {
        const statePutBody = denormaliseGetStartedData(state, values);
        await fetchPutAndGetState({
          body: statePutBody,
          stateKey: stateKeyFromContext,
        });
      }

      if (!hadCompleteApplication) {
        await mutatePutApplication({
          stateKey: stateKeyFromContext,
          body: getUpdatedApplicationBody(application, values, locale),
        });
      }
    }
  };

  /**
   * Add data layer request when submission succeeds.
   * Set applicationData to match current webflow implementation.
   */
  const postGoogleTrackingSuccess = (
    values: TGetStartedDataUK | TGetStartedDataDE,
  ) => {
    const applicationData = {
      event: DATA_LAYER_EVENT,
      applicationData: {
        applicantEmailAddress: values.email,
        requestedAmount: currencyToNumber(values.amount, {
          currencySymbol: true,
          locale: locale === 'de' ? 'de-DE' : locale,
          minorUnits: false,
        }),
        email_marketing_opt_in: !!values.marketingOptIn,
        lengthOfLoan: values.loanDuration,
        purpose: values.loanPurpose,
        ...(locale === 'de'
          ? { detailed_purpose: values.loanPurposeDetails }
          : {}),
      },
    };

    postToDataLayer(applicationData);
  };

  return {
    submitGetStarted,
    postGoogleTrackingSuccess,
  };
};
